import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiTrendingUp, FiUsers, FiStar, FiChevronDown, FiChevronUp, FiCheck } = FiIcons;

const stats = [
  { value: '500+', label: 'Cleaning Businesses Served' },
  { value: '12K+', label: 'Leads Delivered Monthly' },
  { value: '98%', label: 'Client Retention Rate' },
  { value: '4%', label: 'Avg Monthly Growth' },
];

const cases = [
  {
    name: 'Paul Fried',
    business: 'Maid Service',
    location: 'Hoboken, NJ',
    avatar: '/images/avatar-2.webp',
    img: '/images/casestudy-1.webp',
    tag: 'SEO',
    tagColor: 'bg-[#8cc63f] text-white',
    result: '+4% Monthly Growth',
    resultColor: 'text-[#8cc63f]',
    quote: "Get Cleaning Clicks has been doing our SEO for over 3 years. We are currently growing at 4% per month from Google Search alone. Their team knows the cleaning industry inside and out.",
    metrics: [
      { label: 'Monthly Growth', value: '4%' },
      { label: 'Years with GCC', value: '3+' },
      { label: 'Traffic Increase', value: '210%' },
    ],
    services: ['Search Engine Optimization', 'Local Maps SEO', 'Content Marketing'],
    story: "Paul came to us with a maid service that had a decent reputation locally but virtually no online presence. Within the first 6 months, we had him ranking on page 1 for his top keywords. Three years later, his business continues to grow month over month thanks to a consistent SEO strategy.",
  },
  {
    name: 'Maria G.',
    business: 'Sparkle Clean Co.',
    location: 'Dallas, TX',
    avatar: '/images/avatar-1.webp',
    img: '/images/casestudy-2.webp',
    tag: 'Google Ads',
    tagColor: 'bg-blue-600 text-white',
    result: '+$8,400/mo Revenue',
    resultColor: 'text-blue-600',
    quote: "Before Cleaning Clicks, I was spending $2,000/mo on Google Ads with barely any results. Now I get 20-30 qualified leads per month and my schedule is completely full.",
    metrics: [
      { label: 'New Leads/Month', value: '25+' },
      { label: 'Extra Revenue', value: '$8.4K' },
      { label: 'Ad ROI', value: '320%' },
    ],
    services: ['Google Ads', 'Landing Page Design', 'Lead Tracking'],
    story: "Maria was burning through her ad budget with a generic agency that didn't understand the cleaning industry. We rebuilt her entire Google Ads structure from scratch, created dedicated landing pages for each service, and implemented proper conversion tracking. The results were immediate.",
  },
  {
    name: 'James T.',
    business: 'ProShine Cleaning',
    location: 'Atlanta, GA',
    avatar: '/images/avatar-2.webp',
    img: '/images/casestudy-1.webp',
    tag: 'Facebook Ads',
    tagColor: 'bg-indigo-600 text-white',
    result: '+$5,200/mo Revenue',
    resultColor: 'text-indigo-600',
    quote: "I was skeptical at first, but within the first week I had 5 new clients booked. The leads are real, they answer the phone, and they actually want cleaning services.",
    metrics: [
      { label: 'First Week Clients', value: '5' },
      { label: 'Extra Revenue', value: '$5.2K' },
      { label: 'Cost Per Lead', value: '$18' },
    ],
    services: ['Facebook Marketing', 'Reputation Management', 'Marketing Automation'],
    story: "James runs a commercial and residential cleaning operation in Atlanta. He had tried Facebook ads on his own with little success. We restructured his targeting, created compelling ad creatives specific to the cleaning niche, and set up a proper follow-up automation sequence.",
  },
];

const CaseStudyCard = ({ cs, idx }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.15 }}
      className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
    >
      <div className="relative h-56 overflow-hidden">
        <img src={cs.img} alt={cs.business} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full ${cs.tagColor}`}>{cs.tag}</span>
        <div className={`absolute bottom-4 left-4 text-2xl font-black ${cs.resultColor} drop-shadow-lg`}>{cs.result}</div>
      </div>

      <div className="p-7">
        <div className="flex items-center gap-3 mb-4">
          <img src={cs.avatar} alt={cs.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#8cc63f]/30" />
          <div>
            <p className="font-bold text-gray-900">{cs.name}</p>
            <p className="text-gray-500 text-sm">{cs.business} — {cs.location}</p>
          </div>
        </div>

        <div className="flex gap-1 mb-3">
          {[...Array(5)].map((_, i) => <FiStar key={i} className="text-yellow-400 fill-yellow-400 text-sm" />)}
        </div>

        <p className="text-gray-600 text-sm leading-relaxed italic mb-5">"{cs.quote}"</p>

        <div className="grid grid-cols-3 gap-3 mb-5">
          {cs.metrics.map((m) => (
            <div key={m.label} className="text-center bg-gray-50 rounded-xl p-3">
              <div className="text-lg font-black text-[#8cc63f]">{m.value}</div>
              <div className="text-xs text-gray-500">{m.label}</div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-semibold text-[#8cc63f] hover:text-[#7ab535] transition-colors"
        >
          {expanded ? 'Hide Full Story' : 'Read Full Story'}
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-4 border-t border-gray-100 mt-4">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{cs.story}</p>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Services Used</p>
                  <div className="flex flex-wrap gap-2">
                    {cs.services.map((s) => (
                      <span key={s} className="flex items-center gap-1 text-xs bg-[#8cc63f]/10 text-[#5a8a1f] font-semibold px-3 py-1 rounded-full">
                        <FiCheck className="text-[10px]" /> {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const CaseStudies = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="relative bg-[#4b4b4b] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#8cc63f] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#8cc63f] rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[#8cc63f] font-semibold text-sm uppercase tracking-widest block mb-3"
          >
            Real Results. Real Businesses.
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6"
          >
            Cleaning Business <span className="text-[#8cc63f]">Case Studies</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-300 text-lg max-w-2xl mx-auto"
          >
            See how cleaning businesses across the US have grown their revenue, leads, and online presence with Get Cleaning Clicks.
          </motion.p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#8cc63f] py-8">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="text-3xl font-black text-white">{s.value}</div>
              <div className="text-white/80 text-sm font-medium">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#8cc63f] font-semibold text-sm uppercase tracking-widest"
            >
              Watch Our Story
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-gray-900 mt-2"
            >
              Why Cleaning Businesses Choose Us
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-[#4b4b4b] cursor-pointer group"
            onClick={() => setPlaying(true)}
          >
            {!playing ? (
              <>
                <img
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=675&fit=crop"
                  alt="Video thumbnail"
                  className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#8cc63f] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                    <FiPlay className="text-white text-3xl ml-1" />
                  </div>
                  <p className="text-white font-semibold mt-4 text-lg">Watch: How We Grow Cleaning Businesses</p>
                  <p className="text-gray-300 text-sm mt-1">3:42 min</p>
                </div>
              </>
            ) : (
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Get Cleaning Clicks Case Study Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight"
            >
              Client <span className="text-[#8cc63f]">Success Stories</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-gray-500 mt-3 text-lg max-w-xl mx-auto"
            >
              Real cleaning businesses. Real results. No fluff.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cases.map((cs, idx) => (
              <CaseStudyCard key={idx} cs={cs} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#4b4b4b]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white mb-6"
          >
            Ready to Be Our Next <span className="text-[#8cc63f]">Success Story?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gray-300 text-lg mb-10"
          >
            Join 500+ cleaning businesses that trust Get Cleaning Clicks to grow their revenue every single month.
          </motion.p>
          <motion.a
            href="#strategy"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#8cc63f] hover:bg-[#7ab535] text-white font-bold rounded-full transition-all shadow-2xl text-lg"
          >
            <FiTrendingUp /> Get My Free Strategy Session
          </motion.a>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;